import { AgTechFormContext } from "@agtech/core/forms/AgTechFormContext";
import { AgTechFormSection } from "@agtech/web/components/Forms/Layout/AgTechFormSection";
import { SaleInvoiceDialogFormContext, SaleInvoiceDialogFormData } from "../InvoiceCreationDialog";
import AgTechFormBody from "@agtech/web/components/Forms/Layout/AgTechFormBody";
import ContactCreditDialog from "../../../../contacts/dialogs/credits/SaleContactCreditDialog";
import SaleDiscountDialog from "../../../../discounts/dialogs/SaleDiscountDialog";
import { useAgTechQueryCombination } from "@agtech/core/data/queries/AgTechDataQueries";
import { DiscountDataGetAllDiscountsQuery } from "@sales/data/sales/discounts/SaleDiscountDataQueries";
import { SaleContactDataGetContactDetailsQuery } from "@sales/data/operation/contacts/SaleContactDataQueries";
import AgTechQueryLoadingSurface from "@agtech/core/data/queries/AgTechQueryLoadingSurface";
import InvoiceChangeDiscountsGrid from "../../../components/grids/InvoiceChangeDiscountsGrid";
import { buildGridEntitySelectionIndex, markGridSelectionEntityAsSelected } from "@agtech/web/components/Grids/AgTechGridEntities";
import InvoiceChangeCreditsGrid from "../../../components/grids/InvoiceChangeCreditsGrid";

const InvoiceDiscountsAndCreditsPage = (props: {
    formContext: AgTechFormContext<SaleInvoiceDialogFormData, SaleInvoiceDialogFormContext>,
}) => {
    const saleBidderContact = props.formContext.context.contactDetails.contact;
    const pageQuery = useInvoiceDiscountsAndCreditsPageQuery(props);

    return (
        <AgTechQueryLoadingSurface query={pageQuery}>
            {({ discounts, contactDetails }) => (
                <AgTechFormBody>
                    <AgTechFormSection
                        header={`Discounts (${Object.keys(discounts).length})`}
                        classes='flexed'
                        bodyClasses='flexed py-3'
                        headerContent={() => (
                            <SaleDiscountDialog.Button
                                classes='button-thin button-link !pr-0'
                            />
                        )}
                    >
                        <InvoiceChangeDiscountsGrid
                            discountSelections={buildGridEntitySelectionIndex({
                                entities: Object.values(props.formContext.entity.appliedDiscounts),
                                isSelected: e => props.formContext.entity.appliedDiscounts[e.id] !== undefined
                            })}
                            onDiscountSelected={async discount => {
                                props.formContext.updateFormEntity(curr => {
                                    return {
                                        ...curr,
                                        appliedDiscounts: {
                                            ...curr.appliedDiscounts,
                                            [discount.id]: discount
                                        }
                                    }
                                });
                            }}
                            onDiscountDeselected={async discount => {
                                props.formContext.updateFormEntity(curr => {
                                    delete curr.appliedDiscounts[discount.id];

                                    return {
                                        ...curr,
                                        appliedDiscounts: {
                                            ...curr.appliedDiscounts
                                        }
                                    }
                                });
                            }}
                        />
                    </AgTechFormSection>
                    <AgTechFormSection
                        header={`Credits (${Object.keys(contactDetails.credits).length})`}
                        classes='flexed'
                        bodyClasses='flexed py-3'
                        headerContent={() => (
                            <ContactCreditDialog.Button
                                contactId={saleBidderContact.id}
                                classes='button-thin button-link !pr-0'
                            />
                        )}
                    >
                        <InvoiceChangeCreditsGrid
                            contactId={props.formContext.entity.contactId}
                            credits={buildGridEntitySelectionIndex({
                                entities: Object.values(props.formContext.entity.appliedCredits),
                                isSelected: e => props.formContext.entity.appliedCredits[e.id] !== undefined
                            })}
                            onCreditSelected={async credit => {
                                props.formContext.updateFormEntity(curr => {
                                    return {
                                        ...curr,
                                        appliedCredits: {
                                            ...curr.appliedCredits,
                                            [credit.id]: credit
                                        }
                                    }
                                });
                            }}
                            onCreditDeselected={async credit => {
                                props.formContext.updateFormEntity(curr => {
                                    delete curr.appliedCredits[credit.id];

                                    return {
                                        ...curr,
                                        appliedCredits: {
                                            ...curr.appliedCredits
                                        }
                                    }
                                });
                            }}
                        />
                    </AgTechFormSection>
                </AgTechFormBody>
            )}
        </AgTechQueryLoadingSurface>
    )
}

const useInvoiceDiscountsAndCreditsPageQuery = (props: { 
    formContext: AgTechFormContext<SaleInvoiceDialogFormData, SaleInvoiceDialogFormContext>
}) => {
    return useAgTechQueryCombination({
        firstQuery: {
            query: DiscountDataGetAllDiscountsQuery
        },
        secondQuery: {
            query: SaleContactDataGetContactDetailsQuery,
            queryContext: {
                contactId: props.formContext.entity.contactId
            }
        },
        projection: (discounts, contactDetails) => ({
            discounts,
            contactDetails
        })
    })
}

export default InvoiceDiscountsAndCreditsPage;